import React from 'react';
import { TagIcon, ThumbsUpIcon, MessageSquareIcon, ExternalLinkIcon } from 'lucide-react';
interface PersonCardProps {
  person: {
    id: number;
    name: string;
    title: string;
    company: string;
    image: string;
    tags: string[];
    interactions: {
      postId: number;
      type: string;
      comment?: string;
      date: string;
    }[];
    relevance: string;
    status: string;
  };
  onSelect: (personId: number) => void;
  isSelected?: boolean;
  showActions?: boolean;
  onMarkInteresting?: (personId: number) => void;
  onMarkNotRelevant?: (personId: number) => void;
}
export const PersonCard: React.FC<PersonCardProps> = ({
  person,
  onSelect,
  isSelected = false,
  showActions = true,
  onMarkInteresting,
  onMarkNotRelevant
}) => {
  // Calculate engagement score (1-10) based on number and recency of interactions
  const calculateEngagementScore = () => {
    const baseScore = Math.min(person.interactions.length * 2, 6);
    const recencyBonus = person.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 2) ? 2 : 0;
    const commentBonus = person.interactions.some(i => i.comment) ? 2 : 0;
    return Math.min(baseScore + recencyBonus + commentBonus, 10);
  };
  const engagementScore = calculateEngagementScore();
  return <div className={`bg-white rounded-lg shadow-sm border p-4 transition-all cursor-pointer ${isSelected ? 'border-[#f9b30f] ring-2 ring-[#f9b30f] ring-opacity-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => onSelect(person.id)} data-id="element-702">
      <div className="flex items-center mb-3" data-id="element-703">
        <img src={person.image} alt={person.name} className="w-12 h-12 rounded-full mr-3 border border-gray-200" data-id="element-704" />
        <div data-id="element-705">
          <div className="font-medium text-gray-900 flex items-center" data-id="element-706">
            {person.name}
            {person.relevance === 'high' && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-707">
                High Intent
              </span>}
          </div>
          <div className="text-sm text-gray-500" data-id="element-708">{person.title}</div>
          <div className="text-sm text-gray-500 flex items-center mt-0.5" data-id="element-709">
            <ExternalLinkIcon size={12} className="mr-1" data-id="element-710" />
            {person.company}
          </div>
        </div>
        <div className="ml-auto text-right" data-id="element-711">
          <div className="flex flex-col items-end" data-id="element-712">
            <div className="text-xs text-gray-500 mb-1" data-id="element-713">Engagement</div>
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-714">
              <span className={`text-sm font-medium ${engagementScore >= 8 ? 'text-green-600' : engagementScore >= 5 ? 'text-[#f9b30f]' : 'text-gray-600'}`} data-id="element-715">
                {engagementScore}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap gap-2" data-id="element-716">
        {person.tags.slice(0, 3).map((tag, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-717">
            <TagIcon size={12} className="mr-1" data-id="element-718" />
            {tag}
          </span>)}
        {person.tags.length > 3 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-719">
            +{person.tags.length - 3} more
          </span>}
      </div>
      <div className="mb-4" data-id="element-720">
        <div className="text-sm text-gray-700 font-medium mb-1" data-id="element-721">
          Recent Interactions:
        </div>
        {person.interactions.slice(0, 2).map((interaction, index) => <div key={index} className="flex items-start text-sm text-gray-600 mb-1" data-id="element-722">
            {interaction.type === 'liked' ? <ThumbsUpIcon size={14} className="mr-1 mt-0.5 flex-shrink-0" data-id="element-723" /> : <MessageSquareIcon size={14} className="mr-1 mt-0.5 flex-shrink-0" data-id="element-724" />}
            <div data-id="element-725">
              <span className="font-medium" data-id="element-726">{interaction.type}</span> a post{' '}
              {interaction.date}
              {interaction.comment && <div className="mt-1 text-xs bg-gray-50 p-2 rounded italic" data-id="element-727">
                  "
                  {interaction.comment.length > 60 ? `${interaction.comment.substring(0, 60)}...` : interaction.comment}
                  "
                </div>}
            </div>
          </div>)}
        {person.interactions.length > 2 && <div className="text-xs text-[#f9b30f] mt-1 cursor-pointer hover:underline" data-id="element-728">
            +{person.interactions.length - 2} more interactions
          </div>}
      </div>
      {showActions && <div className="flex justify-between pt-2 border-t border-gray-100" data-id="element-729">
          <div data-id="element-730">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${person.relevance === 'high' ? 'bg-green-100 text-green-800' : person.relevance === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-731">
              {person.relevance.charAt(0).toUpperCase() + person.relevance.slice(1)}{' '}
              relevance
            </span>
          </div>
          <div className="flex space-x-2" data-id="element-732">
            <button onClick={e => {
          e.stopPropagation();
          onMarkNotRelevant && onMarkNotRelevant(person.id);
        }} className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200" data-id="element-733">
              Skip
            </button>
            <button onClick={e => {
          e.stopPropagation();
          onMarkInteresting && onMarkInteresting(person.id);
        }} className="px-3 py-1 text-xs font-medium text-white bg-[#f9b30f] rounded hover:bg-[#e0a40e]" data-id="element-734">
              Interesting
            </button>
          </div>
        </div>}
    </div>;
};